/**
 * Product detail full page.
 *
 * Tabs: Overview · BOM · Pricing · Stock.
 *
 * Mock-aware. Pricing tab is a placeholder that links out to that module.
 * Stock tab reads live data from usePersistentMockState (stockMovements).
 */

import { useMemo, useState } from "react";
import {
  createFileRoute,
  Link,
  useNavigate,
} from "@tanstack/react-router";
import {
  ArrowLeft,
  Boxes,
  ChevronRight,
  Copy,
  ExternalLink,
  Pencil,
  Tag,
  Warehouse,
} from "lucide-react";

import { formatMoney } from "@bizop/shared/currency";
import {
  Badge,
  Button,
  EmptyState,
  Sheet,
  SheetBody,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  StatusPill,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  cn,
  toast,
} from "@bizop/ui-kit";

import { PageHeader } from "../../../components/PageHeader";
import {
  ProductForm,
  type ProductFormValues,
} from "../../../components/forms/ProductForm";
import { RequirePermission } from "../../../lib/permissions";
import { usePersistentMockState } from "../../../lib/usePersistentMockState";
import {
  mockBoms,
  mockBomLines,
  mockBrands,
  mockCategories,
  mockProducts,
  mockTaxRates,
  mockUnits,
  type MockBom,
  type MockBomLine,
  type MockBrand,
  type MockCategory,
  type MockProduct,
  type MockTaxRate,
  type MockUnit,
} from "../../../lib/catalogMock";
import {
  mockStockMovements,
  mockStockReservations,
  mockWarehouses,
  type MockStockMovement,
  type MockStockReservation,
  type MockWarehouse,
} from "../../../lib/inventoryMock";
import {
  MovementTypeBadge,
} from "../../../components/MovementTypeBadge";
import { useHasPermission } from "../../../lib/permissions";

export const Route = createFileRoute("/_app/catalog/products/$productId")({
  component: ProductDetailPage,
});

const KIND_LABEL: Record<MockProduct["productKind"], string> = {
  manufactured: "Manufactured",
  imported: "Imported",
  service: "Service",
};

function relativeTime(ms: number): string {
  const diff = Date.now() - ms;
  const min = Math.floor(diff / 60_000);
  if (min < 1) return "just now";
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.floor(hr / 24);
  if (day < 30) return `${day}d ago`;
  return new Date(ms).toLocaleDateString("en-BD");
}

function ProductDetailPage() {
  const { productId } = Route.useParams();
  const navigate = useNavigate();

  const [products, setProducts] = usePersistentMockState<MockProduct[]>("products", mockProducts);
  const [brands] = usePersistentMockState<MockBrand[]>("brands", mockBrands);
  const [categories] = usePersistentMockState<MockCategory[]>("categories", mockCategories);
  const [units] = usePersistentMockState<MockUnit[]>("units", mockUnits);
  const [taxRates] = usePersistentMockState<MockTaxRate[]>("taxRates", mockTaxRates);
  const [boms] = usePersistentMockState<MockBom[]>("boms", mockBoms);
  const [bomLines] = usePersistentMockState<MockBomLine[]>("bomLines", mockBomLines);

  const product = useMemo<MockProduct | null>(
    () => products.find((p) => p._id === productId) ?? null,
    [productId, products],
  );

  const [editingProduct, setEditingProduct] = useState<MockProduct | null>(null);

  if (!product) {
    return (
      <div className="flex flex-col gap-6">
        <PageHeader
          title="Product not found"
          actions={
            <Button variant="outline" onClick={() => navigate({ to: "/catalog" })}>
              <ArrowLeft className="h-4 w-4" /> Back to products
            </Button>
          }
        />
        <EmptyState
          title="Unknown product"
          description={`No product with id ${productId}.`}
        />
      </div>
    );
  }

  const brand = brands.find((b) => b._id === product.brandId) ?? null;
  const category =
    categories.find((c) => c._id === product.categoryId) ?? null;
  const unit = units.find((u) => u._id === product.primaryUnitId) ?? null;
  const taxRate =
    taxRates.find((t) => t._id === product.defaultTaxRateId) ?? null;

  const productBoms = boms.filter((b) => b.productId === product._id);
  const activeBom = productBoms.find((b) => b.isActive) ?? null;
  const activeBomLines = activeBom
    ? bomLines.filter((l) => l.bomId === activeBom._id)
    : [];

  function handleEditSubmit(values: ProductFormValues) {
    // Mock-only: pretend success.
    toast.success(`Saved changes to ${values.sku}`);
    setEditingProduct(null);
  }

  function handleDuplicate() {
    const newId = crypto.randomUUID();
    const clone: MockProduct = {
      ...product,
      _id: newId,
      sku: `${product.sku}-COPY`,
      displayName: `${product.displayName} (Copy)`,
      createdAt: Date.now(),
      updatedAt: Date.now(),
    };
    setProducts((prev) => [clone, ...prev]);
    toast.success("Product duplicated successfully");
    navigate({ to: "/catalog/products/$productId", params: { productId: newId } });
  }

  return (
    <div className="flex flex-col gap-6">
      <Breadcrumbs sku={product.sku} name={product.displayName} />

      <PageHeader
        title={product.displayName}
        description={
          [
            product.sku,
            KIND_LABEL[product.productKind],
            brand?.displayName,
            category?.displayName,
          ]
            .filter(Boolean)
            .join(" · ") || undefined
        }
        actions={
          <div className="flex items-center gap-2">
            <StatusPill variant={product.isActive ? "success" : "neutral"}>
              {product.isActive ? "Active" : "Archived"}
            </StatusPill>
            <Button variant="outline" size="sm" onClick={handleDuplicate}>
              <Copy className="h-4 w-4" /> Duplicate
            </Button>
            <Button variant="ghost" size="icon" onClick={() => setEditingProduct(product)} aria-label="Edit">
              <Pencil className="size-4" />
            </Button>
          </div>
        }
      />

      <Tabs defaultValue="overview">
        <TabsList>
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="bom">BOM</TabsTrigger>
          <TabsTrigger value="pricing">Pricing</TabsTrigger>
          <TabsTrigger value="stock">Stock</TabsTrigger>
        </TabsList>

        <TabsContent value="overview">
          <OverviewTab
            product={product}
            brand={brand?.displayName ?? null}
            category={category?.displayName ?? null}
            unit={unit ? `${unit.displayName} (${unit.code})` : null}
            taxRate={taxRate?.displayName ?? null}
            onEdit={() => setEditingProduct(product)}
          />
        </TabsContent>

        <TabsContent value="bom">
          <BomTab activeBom={activeBom} activeLines={activeBomLines} products={products} units={units} />
        </TabsContent>

        <TabsContent value="pricing">
          <PricingTab />
        </TabsContent>

        <TabsContent value="stock">
          <StockTab productId={product._id} />
        </TabsContent>
      </Tabs>

      {/* Edit Sheet */}
      <Sheet
        open={editingProduct !== null}
        onOpenChange={(open) => {
          if (!open) setEditingProduct(null);
        }}
      >
        <SheetContent side="right" size="md">
          {editingProduct ? (
            <>
              <SheetHeader>
                <SheetTitle>Edit {editingProduct.displayName}</SheetTitle>
                <SheetDescription>SKU is immutable.</SheetDescription>
              </SheetHeader>
              <SheetBody>
                <ProductForm
                  brands={brands.map((b) => ({
                    id: b._id,
                    label: b.displayName,
                  }))}
                  categories={categories.map((c) => ({
                    id: c._id,
                    label: c.displayName,
                  }))}
                  units={units.map((u) => ({
                    id: u._id,
                    label: `${u.displayName} (${u.code})`,
                  }))}
                  taxRates={taxRates.map((t) => ({
                    id: t._id,
                    label: t.displayName,
                  }))}
                  skuLocked
                  submitLabel="Save changes"
                  initialValues={{
                    sku: editingProduct.sku,
                    displayName: editingProduct.displayName,
                    description: editingProduct.description ?? "",
                    productKind: editingProduct.productKind,
                    brandId: editingProduct.brandId,
                    categoryId: editingProduct.categoryId,
                    primaryUnitId: editingProduct.primaryUnitId,
                    defaultTaxRateId: editingProduct.defaultTaxRateId,
                    hsCode: editingProduct.hsCode ?? "",
                    standardCostCents: editingProduct.standardCostCents,
                    isActive: editingProduct.isActive,
                    isSellable: editingProduct.isActive,
                    isPurchasable: editingProduct.productKind !== "service",
                  }}
                  onSubmit={handleEditSubmit}
                  onCancel={() => setEditingProduct(null)}
                />
              </SheetBody>
            </>
          ) : null}
        </SheetContent>
      </Sheet>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────
 * Sub-components
 * ───────────────────────────────────────────────────────────── */

function Breadcrumbs({ sku, name }: { sku: string; name: string }) {
  return (
    <nav className="flex items-center gap-1 text-[12px] text-(--text-tertiary)">
      <Link to="/catalog" className="hover:text-(--text-secondary)">
        Catalog
      </Link>
      <ChevronRight className="h-3 w-3" aria-hidden />
      <Link to="/catalog" className="hover:text-(--text-secondary)">
        Products
      </Link>
      <ChevronRight className="h-3 w-3" aria-hidden />
      <span className="text-(--text-primary)">
        {name}{" "}
        <span className="font-mono text-(--text-tertiary)">({sku})</span>
      </span>
    </nav>
  );
}

function OverviewTab({
  product,
  brand,
  category,
  unit,
  taxRate,
  onEdit,
}: {
  product: MockProduct;
  brand: string | null;
  category: string | null;
  unit: string | null;
  taxRate: string | null;
  onEdit: () => void;
}) {
  return (
    <section className="rounded-lg border border-(--border-subtle) bg-(--bg-surface) p-5">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-[14px] font-semibold text-(--text-primary)">
          Details
        </h2>
        <Button variant="ghost" size="icon" onClick={onEdit} aria-label="Edit">
          <Pencil className="size-4" />
        </Button>
      </div>
      <dl className="grid grid-cols-2 sm:grid-cols-3 gap-x-6 gap-y-4">
        <DetailItem label="SKU" mono>
          {product.sku}
        </DetailItem>
        <DetailItem label="Kind">{KIND_LABEL[product.productKind]}</DetailItem>
        <DetailItem label="Brand">{brand ?? "—"}</DetailItem>
        <DetailItem label="Category">{category ?? "—"}</DetailItem>
        <DetailItem label="Base Unit">{unit ?? "—"}</DetailItem>
        <DetailItem label="Default Tax">{taxRate ?? "—"}</DetailItem>
        <DetailItem label="HS Code" mono>
          {product.hsCode ?? "—"}
        </DetailItem>
        <DetailItem label="Reorder Qty">
          {product.reorderQuantity != null ? product.reorderQuantity : "—"}
        </DetailItem>
        <DetailItem label="Barcode" mono>
          {product.barcodePrimary ?? "—"}
        </DetailItem>
        <RequirePermission permission="finance.read">
          <DetailItem label="Standard Cost">
            <span className="font-mono">
              {formatMoney(product.standardCostCents, "BDT", { locale: "en-BD" })}
            </span>
          </DetailItem>
        </RequirePermission>
        <DetailItem label="Updated">{relativeTime(product.updatedAt)}</DetailItem>
        <DetailItem label="Status">
          <StatusPill variant={product.isActive ? "success" : "neutral"}>
            {product.isActive ? "Active" : "Archived"}
          </StatusPill>
        </DetailItem>
      </dl>

      {product.description ? (
        <div className="mt-6">
          <div className="text-[11px] font-medium uppercase tracking-wide text-(--text-tertiary) mb-1">
            Description
          </div>
          <p className="text-[13px] text-(--text-primary) whitespace-pre-wrap">
            {product.description}
          </p>
        </div>
      ) : null}
    </section>
  );
}

function BomTab({
  activeBom,
  activeLines,
  products,
  units,
}: {
  activeBom: MockBom | null;
  activeLines: MockBomLine[];
  products: MockProduct[];
  units: MockUnit[];
}) {
  if (!activeBom) {
    return (
      <EmptyState
        title="No active BOM"
        description="Define a Bill of Materials for this product to enable manufacturing planning."
        action={
          <Link to="/catalog/bom">
            <Button variant="outline">
              <Boxes className="h-4 w-4" /> Open BOM module
            </Button>
          </Link>
        }
      />
    );
  }
  return (
    <section className="rounded-lg border border-(--border-subtle) bg-(--bg-surface) p-5">
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="flex items-center gap-2">
            <h2 className="text-[14px] font-semibold text-(--text-primary)">
              {activeBom.code}
            </h2>
            <Badge variant="success" className="font-mono">
              {activeBom.versionLabel}
            </Badge>
          </div>
          <p className="text-[12px] text-(--text-tertiary)">
            Output: {activeBom.outputQuantity} ·{" "}
            {units.find((u) => u._id === activeBom.outputUnitId)?.code ?? "—"}
          </p>
        </div>
        <Link to="/catalog/bom">
          <Button variant="outline" size="sm">
            <ExternalLink className="h-3.5 w-3.5" /> Open BOM
          </Button>
        </Link>
      </div>

      <div className="rounded-md border border-(--border-subtle) overflow-hidden">
        <table className="w-full text-[13px]">
          <thead>
            <tr className="text-left text-[11px] font-medium uppercase tracking-wide text-(--text-tertiary) bg-(--bg-canvas)">
              <th className="px-3 py-2">Component</th>
              <th className="px-3 py-2 text-right">Qty</th>
              <th className="px-3 py-2">Unit</th>
              <th className="px-3 py-2 text-right">Scrap</th>
            </tr>
          </thead>
          <tbody>
            {activeLines.length === 0 ? (
              <tr>
                <td
                  colSpan={4}
                  className="px-3 py-6 text-center text-(--text-tertiary)"
                >
                  No components defined
                </td>
              </tr>
            ) : (
              activeLines
                .slice()
                .sort((a, b) => a.sortOrder - b.sortOrder)
                .map((l) => {
                  const c = products.find(
                    (p) => p._id === l.componentProductId,
                  );
                  const unit = units.find((u) => u._id === l.unitId);
                  return (
                    <tr
                      key={l._id}
                      className="border-t border-(--border-subtle)"
                    >
                      <td className="px-3 py-2">
                        <div className="flex flex-col">
                          <span className="text-(--text-primary)">
                            {c?.displayName ?? l.componentProductId}
                          </span>
                          <span className="font-mono text-[11px] text-(--text-tertiary)">
                            {c?.sku ?? "—"}
                          </span>
                        </div>
                      </td>
                      <td className="px-3 py-2 text-right font-mono tabular-nums">
                        {l.quantity}
                      </td>
                      <td className="px-3 py-2 text-(--text-secondary)">
                        {unit?.displayName ?? l.unitId}
                      </td>
                      <td className="px-3 py-2 text-right font-mono tabular-nums">
                        {(Number(l.wastageAllowanceE4) / 10_000).toFixed(2)}%
                      </td>
                    </tr>
                  );
                })
            )}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function PricingTab() {
  return (
    <EmptyState
      title="Pricing not yet wired"
      description="Once the Pricing module ships, price-list entries that include this product will appear here."
      action={
        <Link to="/pricing">
          <Button variant="outline">
            <Tag className="h-4 w-4" /> Open Pricing
          </Button>
        </Link>
      }
    />
  );
}

function StockTab({ productId }: { productId: string }) {
  const canSeeCost = useHasPermission("finance.read");
  const [movements] = usePersistentMockState<MockStockMovement[]>("stockMovements", mockStockMovements);
  const [reservations] = usePersistentMockState<MockStockReservation[]>("stockReservations", mockStockReservations);
  const [warehouses] = usePersistentMockState<MockWarehouse[]>("warehouses", mockWarehouses);

  const warehouseById = useMemo(
    () => new Map(warehouses.map((w) => [w._id, w])),
    [warehouses],
  );

  // Filter movements for this product and aggregate per warehouse
  const productMovements = useMemo(
    () =>
      movements
        .filter((m) => m.productId === productId)
        .slice()
        .sort((a, b) => b.occurredAt - a.occurredAt),
    [movements, productId],
  );

  const productReservations = useMemo(
    () => reservations.filter((r) => r.productId === productId && r.status === "active"),
    [reservations, productId],
  );

  // Aggregate on-hand per warehouse
  type StockSummary = {
    warehouseId: string;
    warehouseName: string;
    onHand: number;
    reserved: number;
    available: number;
  };

  const stockByWarehouse = useMemo<StockSummary[]>(() => {
    const map = new Map<string, StockSummary>();
    for (const m of productMovements) {
      let cell = map.get(m.warehouseId);
      if (!cell) {
        const wh = warehouseById.get(m.warehouseId);
        cell = {
          warehouseId: m.warehouseId,
          warehouseName: wh?.displayName ?? m.warehouseId,
          onHand: 0,
          reserved: 0,
          available: 0,
        };
        map.set(m.warehouseId, cell);
      }
      cell.onHand += m.quantityDelta;
    }
    for (const r of productReservations) {
      const cell = map.get(r.warehouseId);
      if (cell) cell.reserved += r.quantityReserved;
    }
    for (const cell of map.values()) {
      cell.available = cell.onHand - cell.reserved;
    }
    return Array.from(map.values()).filter((c) => c.onHand !== 0);
  }, [productMovements, productReservations, warehouseById]);

  const totalOnHand = stockByWarehouse.reduce((s, c) => s + c.onHand, 0);

  if (productMovements.length === 0) {
    return (
      <EmptyState
        title="No stock tracked"
        description="No stock movements have been recorded for this product yet."
        action={
          <Link to="/inventory">
            <Button variant="outline">
              <Warehouse className="h-4 w-4" /> Open Inventory
            </Button>
          </Link>
        }
      />
    );
  }

  return (
    <div className="flex flex-col gap-5">
      {/* Summary by warehouse */}
      <section className="rounded-lg border border-(--border-subtle) bg-(--bg-surface) p-5">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-[14px] font-semibold text-(--text-primary)">
            Stock by warehouse
          </h2>
          <Badge variant="neutral" size="sm">
            Total on-hand: {totalOnHand.toLocaleString("en-BD")}
          </Badge>
        </div>
        <div className="rounded-md border border-(--border-subtle) overflow-hidden">
          <table className="w-full text-[13px]">
            <thead>
              <tr className="text-left text-[11px] font-medium uppercase tracking-wide text-(--text-tertiary) bg-(--bg-canvas)">
                <th className="px-3 py-2">Warehouse</th>
                <th className="px-3 py-2 text-right">On-hand</th>
                <th className="px-3 py-2 text-right">Reserved</th>
                <th className="px-3 py-2 text-right">Available</th>
              </tr>
            </thead>
            <tbody>
              {stockByWarehouse.map((cell) => (
                <tr key={cell.warehouseId} className="border-t border-(--border-subtle)">
                  <td className="px-3 py-2 text-(--text-primary)">{cell.warehouseName}</td>
                  <td className="px-3 py-2 text-right font-mono tabular-nums">{cell.onHand.toLocaleString("en-BD")}</td>
                  <td className={cn("px-3 py-2 text-right font-mono tabular-nums", cell.reserved > 0 ? "text-(--warning)" : "text-(--text-tertiary)")}>
                    {cell.reserved.toLocaleString("en-BD")}
                  </td>
                  <td className={cn("px-3 py-2 text-right font-mono tabular-nums", cell.available <= 0 ? "text-(--danger)" : cell.available < 10 ? "text-(--warning)" : "text-(--text-primary)")}>
                    {cell.available.toLocaleString("en-BD")}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {/* Recent movements */}
      <section className="rounded-lg border border-(--border-subtle) bg-(--bg-surface) p-5">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-[14px] font-semibold text-(--text-primary)">
            Recent movements
          </h2>
          <Link to="/inventory">
            <Button variant="outline" size="sm">
              <Warehouse className="h-3.5 w-3.5" /> Full ledger
            </Button>
          </Link>
        </div>
        <ul className="flex flex-col gap-2">
          {productMovements.slice(0, 10).map((m) => (
            <li
              key={m._id}
              className={cn(
                "rounded-md border border-(--border-subtle) bg-(--bg-canvas) p-3",
                "flex items-start gap-3",
              )}
            >
              <MovementTypeBadge type={m.movementType} />
              <div className="flex-1 min-w-0">
                <div className="flex items-baseline justify-between gap-3">
                  <span
                    className={cn(
                      "font-mono text-[13px] tabular-nums",
                      m.quantityDelta > 0
                        ? "text-(--success)"
                        : m.quantityDelta < 0
                          ? "text-(--danger)"
                          : "text-(--text-tertiary)",
                    )}
                  >
                    {m.quantityDelta > 0 ? "+" : ""}
                    {m.quantityDelta.toLocaleString("en-BD")}
                  </span>
                  <span className="text-[11px] text-(--text-tertiary) tabular-nums">
                    {new Date(m.occurredAt).toLocaleString("en-BD", {
                      dateStyle: "short",
                      timeStyle: "short",
                    })}
                  </span>
                </div>
                <div className="text-[12px] text-(--text-secondary)">
                  {warehouseById.get(m.warehouseId)?.displayName ?? "—"}
                  {canSeeCost ? (
                    <>
                      {" · "}Unit cost{" "}
                      {formatMoney(m.unitCostBaseCents, "BDT", { locale: "en-BD" })}
                    </>
                  ) : null}
                </div>
                {m.notes ? (
                  <div className="mt-1 text-[12px] text-(--text-tertiary) italic">
                    {m.notes}
                  </div>
                ) : null}
              </div>
            </li>
          ))}
          {productMovements.length === 0 ? (
            <li className="rounded-md border border-dashed border-(--border-default) bg-(--bg-canvas) p-4 text-center text-[12px] text-(--text-tertiary)">
              No movements recorded for this product.
            </li>
          ) : null}
        </ul>
      </section>
    </div>
  );
}

function DetailItem({
  label,
  children,
  mono,
}: {
  label: string;
  children: React.ReactNode;
  mono?: boolean;
}) {
  return (
    <div className="flex flex-col gap-0.5">
      <dt className="text-[11px] font-medium uppercase tracking-wide text-(--text-tertiary)">
        {label}
      </dt>
      <dd
        className={cn(
          "text-[13px] text-(--text-primary)",
          mono && "font-mono",
        )}
      >
        {children}
      </dd>
    </div>
  );
}
